import React from "react";
import FlightSearchWidget from "@/components/site/FlightSearchWidget";
import PromoStrip from "@/components/site/PromoStrip";
import { Link } from "react-router-dom";
import { Plane, Building2, Car, Package, MapPin, Sparkles } from "lucide-react";
import { useSettings } from "@/lib/settings";

export default function Home() {
  const { settings } = useSettings();
  const quick = [
    { to: "/flights", label: "Flights", icon: Plane, on: settings.product_flight !== false },
    { to: "/hotels", label: "Hotels", icon: Building2, on: settings.product_hotel !== false },
    { to: "/cars", label: "Cars", icon: Car, on: settings.product_car !== false },
    { to: "/packages", label: "Packages", icon: Package, on: settings.product_package !== false },
    { to: "/sightseeing", label: "Sightseeing", icon: MapPin, on: settings.product_sightseeing !== false },
  ].filter(q => q.on);
  return (
    <>
      {/* HERO */}
      <section className="relative">
        <div className="absolute inset-0">
          <img
            src={settings.hero_image}
            alt="Tropical destination"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[var(--brand-accent)]/85 via-[var(--brand-accent)]/55 to-[var(--brand-primary)]/35" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-12">
          <div className="max-w-3xl text-white">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[var(--brand-secondary)] bg-white/10 backdrop-blur px-3 py-1.5 rounded-full border border-white/20">
              <Sparkles className="w-3.5 h-3.5" /> Curated journeys, all in one place
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter mt-5 text-shadow-strong">
              {settings.hero_title}
            </h1>
            <p className="text-base sm:text-lg text-teal-50/90 mt-4 leading-relaxed max-w-xl">
              {settings.hero_subtitle}
            </p>
          </div>

          <div className="mt-8 flex flex-wrap gap-2">
            {quick.map((q) => (
              <Link
                data-testid={`hero-quicklink-${q.label.toLowerCase()}`}
                key={q.to}
                to={q.to}
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 text-white px-4 py-2 rounded-full text-sm font-medium transition-colors"
              >
                <q.icon className="w-4 h-4" /> {q.label}
              </Link>
            ))}
          </div>

          {settings.product_flight !== false && (
            <div className="mt-8">
              <FlightSearchWidget />
            </div>
          )}
        </div>
      </section>

      <PromoStrip />

      {/* Why us */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { title: "Best price guarantee", desc: "We constantly compare deals across 500+ partners so you never overpay." },
            { title: "Flexible bookings", desc: "Cancel or modify most bookings with a single click from your dashboard." },
            { title: "24×7 human support", desc: "Real humans on call when you need a hand. No bots, no scripts." },
          ].map((c, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-2xl p-7 hover:-translate-y-1 hover:shadow-lg transition-all">
              <div className="w-10 h-10 rounded-xl bg-teal-50 text-[var(--brand-primary)] flex items-center justify-center font-bold">{i + 1}</div>
              <h3 className="font-heading text-xl font-semibold mt-4 text-[var(--brand-accent)]">{c.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed mt-2">{c.desc}</p>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
